import React, { useState } from 'react';
import { pick } from 'lodash';
import { updateProfile, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

import { updateDoc, doc, collection, } from 'firebase/firestore';
import { fieldDisplayValue } from '../../shared/util';
import { fields } from '../../shared/models/user';
import { auth, db, functions } from '../../firebase';
import Page from '../Page';
import useUser from '../hooks/useUser';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import EditButton from '../EditButton';
import ModalButton from '../ModalButton';
import ModelFormModal from '../modals/ModelFormModal';

const { entries } = Object;

const updateEmailFn = httpsCallable(functions, 'updateEmail');

const emailChangeFields = {
  newEmail: {
    ...fields.email,
    label: '新しいメールアドレス',
    placeholder: 'example@example.com',
  },
  currentPassword: fields.currentPassword,
};

export default function Profile() {
  return (
    <Page>
      <PageContent />
    </Page>
  );
}

function PageContent() {
  const { user } = useUser();
  const tenant = useDocumentSubscription(user && doc(collection(db, 'tenants'), user.tenantId), [user]);
  const profileFields = { tenantName: fields.tenantName, ...pick(fields, ['displayName', 'nameKana', 'phone']) };
  const displayFields = pick(fields, ['displayName', 'nameKana', 'email', 'phone']);

  const processValues = async (values) => {
    if (values.displayName !== user.displayName) {
      await updateProfile(auth.currentUser, { displayName: values.displayName });
    }
    if (values.tenantName !== tenant?.name) {
      await updateDoc(tenant.ref, { name: values.tenantName });
    }
    return pick(values, ['displayName', 'nameKana', 'phone']);
  };

  const onSubmitEmailChange = async ({ newEmail, currentPassword }, { onClickClose }) => {
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updateEmailFn({ newEmail });
      toast.success('メールアドレスを変更しました');
      onClickClose();
    } catch (e) {
      console.error(e);
      const message = e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential'
        ? 'パスワードが正しくありません'
        : 'メールアドレスの変更に失敗しました';
      toast.error(message);
    }
  };

  return (
    <div className="container-fluid" style={{ marginTop: 51 }}>
      <div className="px-4">
        <div className="d-flex justify-content-center mb-4">
          <h4>プロフィール</h4>
        </div>

        <div className="row">
          <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3">
            <div className="card p-4">
              <div className="mb-3 d-flex justify-content-end gap-2">
                <ModalButton
                  Modal={ModelFormModal}
                  modalProps={{
                    title: 'メールアドレス変更',
                    fields: emailChangeFields,
                    values: {},
                    onSubmit: onSubmitEmailChange,
                  }}
                >
                  <span className="fas fa-envelope me-1" />
                  メールアドレス変更
                </ModalButton>
                <EditButton
                  itemRef={user?.ref}
                  FormModal={ModelFormModal}
                  formProps={{
                    title: 'プロフィール編集',
                    fields: profileFields,
                    values: { ...user, tenantName: tenant?.name },
                  }}
                  processValues={processValues}
                />
              </div>

              <div>
                {user && (
                  <div>
                    <div className="mb-3">
                      <div className="text-muted small">会社名</div>
                      <div>{tenant?.name || '(未登録)'}</div>
                    </div>
                    {entries(displayFields).map(([fieldName, { label }]) => {
                      const value = fieldDisplayValue(user, fieldName, fields[fieldName]);

                      return (
                        <div className="mb-3" key={fieldName}>
                          <div className="text-muted small">{label}</div>
                          <div>{value || '(未登録)'}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {user && <NotificationSettingsCard user={user} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationSettingsCard({ user }) {
  const settings = user.notificationSettings || {};
  const morningEnabled = settings.dailySummaryMorning ?? true;
  const [saving, setSaving] = useState(false);

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      await updateDoc(user.ref, { [`notificationSettings.${key}`]: value });
    } catch (e) {
      console.error(e);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 mt-4">
      <h6 className="mb-3">デイリーサマリー配信</h6>
      <div className="text-muted small mb-3">
        ダッシュボードの内容をメールでお届けします。
      </div>
      <div className="form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="dailySummaryMorning"
          checked={morningEnabled}
          disabled={saving}
          onChange={(e) => updateSetting('dailySummaryMorning', e.target.checked)}
        />
        <label className="form-check-label" htmlFor="dailySummaryMorning">
          朝9時にメールで受け取る
        </label>
      </div>
    </div>
  );
}
